// src/lib/queryClient.ts
import { QueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

// Create the query client with default options
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache for 5 minutes by default
      staleTime: 5 * 60 * 1000,

      // Keep data for 10 minutes after component unmounts
      cacheTime: 10 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },

      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus for important data
      refetchOnWindowFocus: true,

      // Refetch when reconnecting
      refetchOnReconnect: true,

      // Background refetch interval (30 minutes)
      refetchInterval: 30 * 60 * 1000,
    },
    mutations: {
      // Retry mutations once
      retry: 1,

      // Global error handler for mutations
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Si è verificato un errore';
        toast.error(errorMessage);
      },
    },
  },
});

// Query keys factory for consistent key management
export const queryKeys = {
  // Auth
  auth: {
    user: ['auth', 'user'] as const,
    permissions: ['auth', 'permissions'] as const,
  },

  // Seasons
  seasons: {
    all: ['seasons'] as const,
    current: ['seasons', 'current'] as const,
    detail: (id: string) => ['seasons', 'detail', id] as const,
  },

  games: {
  all: ['gare'] as const,
  list: (filters?: any) => ['gare', 'list', filters ?? {}] as const,
  detail: (id: string | number) => ['gare', 'detail', String(id)] as const,
  upcoming: ['gare', 'upcoming'] as const,
},

designazioni: {
  all: ['designazioni'] as const,
  byGaraS4Y: (id: string | number) => ['designazioni', 'by-gara', String(id)] as const,
  my: ['designazioni', 'my'] as const,
},
  // Gare
  gare: {
    all: ['gare'] as const,
    lists: () => ['gare', 'list'] as const,
    list: (filters: Record<string, any>) => ['gare', 'list', filters] as const,
    detail: (id: string) => ['gare', 'detail', id] as const,
    search: (query: string) => ['gare', 'search', query] as const,
  },

  // Rapportini
  rapportini: {
    all: ['rapportini'] as const,
    lists: () => ['rapportini', 'list'] as const,
    list: (filters: Record<string, any>) => ['rapportini', 'list', filters] as const,
    detail: (id: string) => ['rapportini', 'detail', id] as const,
    byGara: (garaId: string) => ['rapportini', 'by-gara', garaId] as const,
  },

  // CRA01
  cra01: {
    all: ['cra01'] as const,
    lists: () => ['cra01', 'list'] as const,
    list: (filters: Record<string, any>) => ['cra01', 'list', filters] as const,
    detail: (id: string) => ['cra01', 'detail', id] as const,
  },

  // Users (admin)
  users: {
    all: ['users'] as const,
    lists: () => ['users', 'list'] as const,
    list: (filters: Record<string, any>) => ['users', 'list', filters] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },

  // Statistics
  statistics: {
    all: ['statistics'] as const,
    overview: ['statistics', 'overview'] as const,
    gare: (filters: Record<string, any>) => ['statistics', 'gare', filters] as const,
    rapportini: (filters: Record<string, any>) => ['statistics', 'rapportini', filters] as const,
    arbitri: (filters: Record<string, any>) => ['statistics', 'arbitri', filters] as const,

    // ✅ nuove chiavi usate nei grafici
    falliPerMinuto: (params: Record<string, any>) =>
      ['statistics', 'falli-per-minuto', params] as const,

    ammonizioniPerMotivazione: (filters: Record<string, any>) =>
      ['statistics', 'ammonizioni-per-motivazione', filters] as const,

    espulsioniPerMotivazione: (filters: Record<string, any>) =>
      ['statistics', 'espulsioni-per-motivazione', filters] as const,

    sanzioniPerTipo: (filters: Record<string, any>) =>
      ['statistics', 'sanzioni-per-tipo', filters] as const,
  },
};


// Helper functions for cache management
export const cacheUtils = {
  // Invalidate all queries for a specific season
  invalidateSeasonQueries: (seasonId?: string) => {
    if (seasonId) {
      // Invalidate queries that depend on season
      queryClient.invalidateQueries({ queryKey: queryKeys.gare.all, exact: false });
      queryClient.invalidateQueries({ queryKey: queryKeys.rapportini.all, exact: false });
      queryClient.invalidateQueries({ queryKey: queryKeys.designazioni.all, exact: false });
      queryClient.invalidateQueries({ queryKey: queryKeys.cra01.all, exact: false });
      queryClient.invalidateQueries({ queryKey: queryKeys.statistics.all, exact: false });
    }
  },

  // Clear all cache
  clearAll: () => {
    queryClient.clear();
  },

  // Remove specific queries
  removeQueries: (queryKey: readonly unknown[]) => {
    queryClient.removeQueries({ queryKey });
  },

  // Prefetch data
  prefetchGare: (filters: Record<string, any>) => {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.gare.list(filters),
      staleTime: 2 * 60 * 1000, // Consider fresh for 2 minutes
    });
  },

  // Set query data manually
  setQueryData: <T>(queryKey: readonly unknown[], data: T) => {
    queryClient.setQueryData(queryKey, data);
  },

  // Get cached data
  getQueryData: <T>(queryKey: readonly unknown[]): T | undefined => {
    return queryClient.getQueryData<T>(queryKey);
  },
};

// Connection status management
export const connectionMonitor = {
  isOnline: () => navigator.onLine,

  setupListeners: () => {
    const handleOnline = () => {
      // Refetch all active queries when coming back online
      queryClient.refetchQueries();
      toast.success('Connessione ripristinata');
    };

    const handleOffline = () => {
      toast.error('Connessione persa. I dati potrebbero non essere aggiornati.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  },
};

// Dev tools helpers (only in development)
if (import.meta.env.DEV) {
  // Add query client to window for debugging
  (window as any).queryClient = queryClient;
  (window as any).queryKeys = queryKeys;
  (window as any).cacheUtils = cacheUtils;
}
